import React, { useState, useEffect } from 'react';

const Solutions = () => {
  const [showVideo, setShowVideo] = useState(false);
  const [text, setText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const fullText = "Farmnet connects farmers to veterinary care—reducing livestock losses and boosting productivity through digital animal health services.";

  useEffect(() => {
    let currentIndex = 0;
    let typingInterval;

    const typeText = () => {
      if (currentIndex < fullText.length) {
        setText(fullText.substring(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setTimeout(() => {
          setIsTyping(false);
          setTimeout(() => {
            backspaceText();
          }, 2000);
        }, 1000);
      }
    };

    const backspaceText = () => {
      let backspaceInterval;
      let backspaceIndex = fullText.length;

      const eraseText = () => {
        if (backspaceIndex >= 0) {
          setText(fullText.substring(0, backspaceIndex));
          backspaceIndex--;
        } else {
          clearInterval(backspaceInterval);
          setIsTyping(true);
          currentIndex = 0;
          typingInterval = setInterval(typeText, 50);
        }
      };

      backspaceInterval = setInterval(eraseText, 20);
    };

    if (isTyping) {
      typingInterval = setInterval(typeText, 50);
    }

    return () => {
      clearInterval(typingInterval);
    };
  }, [isTyping, fullText]);

  return (
    <div className="relative bg-[#F3F3F3]">
      {/* Content */}
      <div className="relative z-10 px-4 md:px-24 pb-10 md:pt-5 pt-1">
        {/* Heading */}
        <div className="mt-2 md:mt-2 md:mb-5 flex justify-between items-center p mb-2">
          <h1 className="text-[12px] md:text-[36px] font-bold md:mb-3 ">
          <span className='text-[#152C24]'>INSIGHTS & INNOVATIONS</span>
          </h1>
          <div>
            <div className='flex md:gap-2 gap-1 justify-center md:mt-2'>
              <div className='bg-[#CFD7D7] w-[15px] h-[15px] md:w-[40px] md:h-[40px] flex justify-center md:mt-0 rounded-full'> 
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751045210/Expand_left_light_scmuqw.png"
                  alt="Arrow"
                  className="md:w-6 md:h-6 w-3 h-3 mt-2 md:flex hidden"
                />
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751045210/Expand_left_light_scmuqw.png"
                  alt="Arrow"
                  className="w-2 h-2 mt-1 md:hidden flex"
                /> 
              </div>
              <div className="bg-[#2E453D] border-b-2 border-[#FFBD26] md:ml-0 ml-2 text-white font-normal md:px-6 px-2 rounded-md md:rounded-[30px] w-[70px] h-[15px] md:w-[150px] md:text-[20px] text-[6px] md:h-[40px] mt-0 md:-mt-[1px] flex justify-between items-center">
                See More
              </div>
              <div className='bg-[#2E453D] w-[15px] h-[15px] md:w-[40px] md:h-[40px] flex justify-center md:mt-0 mr-2 rounded-full'> 
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1748783006/Expand_right_gzn0vr.png"
                  alt="Arrow"
                  className="md:w-6 md:h-6 w-3 h-3 mt-2 md:flex hidden"
                />
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1748783006/Expand_right_gzn0vr.png"
                  alt="Arrow"
                  className="w-2 h-2 mt-1 md:hidden flex"
                /> 
              </div>
            </div>
          </div>
        </div>

        {/* Records Section */}
        <div className="rounded-md w-full md:h-[630px] h-[175px] relative flex items-center justify-center p-2 md:p-0 pt-2 md:mt-4">
          <div className="rounded-xl overflow-hidden relative w-full h-[180px] md:mt-5 md:h-[700px] flex justify-center items-center cursor-pointer">
              <>
                <img
                  src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1751380764/image-3_tn3oor.png"
                  alt="Mobile"
                  className="object-cover w-full h-full"
                />
              </>
          </div>
        </div>

        {/* Description */}
        <div className='md:text-[32px] text-[10px] font-normal md:mt-16 mt-2 space-y-6'>
          <h1 className="text-[12px] md:text-[36px] font-bold md:mb-3 text-[#979797]">
            INSIGHTS & INNOVATIONS | <span className='text-[#152C24]'>Veterinary Services</span>
          </h1>
          
          <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
            🐄 What Are Digital Veterinary Services?
          </h2>
          
          <p className="md:text-[32px] text-[8px]">
            Digital veterinary services are technology-based solutions that provide animal health support, disease diagnostics, and veterinary advice remotely or through digital platforms. These services help farmers care for their livestock by making veterinary expertise more accessible, affordable, and timely—especially in areas with limited physical access to animal health professionals.
          </p>
          
          <p className="md:text-[32px] text-[8px]">
            They include:
          </p>
          
          <ul className="md:text-[32px] text-[8px] list-disc pl-5 space-y-2">
            <li>Mobile apps and call centers for animal health advice</li>
            <li>Tele-veterinary consultations via phone or video</li>
            <li>Digital disease surveillance and early warning systems</li>
            <li>Livestock treatment schedules and reminders</li>
            <li>E-commerce platforms for veterinary drugs and supplements</li>
            <li>Record-keeping and livestock management tools</li>
          </ul>
          
          <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
            🐐 How Smallholder Farmers in Africa Use Digital Veterinary Services
          </h2>
          
          <p className="md:text-[32px] text-[8px]">
            Smallholder farmers across Africa are increasingly using affordable, mobile based veterinary services to improve livestock health and productivity. Here's how they do it:
          </p>
          
          <ol className="md:text-[32px] text-[8px] list-decimal pl-5 space-y-3 [&>li::marker]:font-bold">
            <li>
              <span className="font-bold">Tele-Veterinary Consultations</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Farmers use mobile phones to call or chat with veterinarians when animals are sick</li>
                <li>This saves travel time and cost, especially in rural or remote areas</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Mobile Apps for Animal Health</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Farmnet provides symptom checkers and diagnosis tools</li>
                <li>Includes treatment guidance and vaccination schedules</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">SMS & USSD Services</span>
              <ul className="list-disc pl-5 mt-1">
                <li>For farmers without smartphones, SMS/USSD services provide animal care tips</li>
                <li>Includes vaccination reminders and disease outbreak alerts</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">E-commerce for Veterinary Products</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Farmers use apps to order veterinary drugs and supplements</li>
                <li>Products can be delivered directly to farms</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Disease Reporting & Early Warning</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Digital platforms collect farmer data to detect outbreaks</li>
                <li>Helps contain diseases early through community alerts</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Livestock Records & Management</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Digital tools track animal health and breeding cycles</li>
                <li>Monitors milk production and expenses for better herd management</li>
              </ul>
            </li>
          </ol>
          
          <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
            📱 Example: How Farmnet Can Support Digital Veterinary Services
          </h2>
          
          <p className="md:text-[32px] text-[8px]">
            If integrated, the Farmnet App can serve smallholder livestock keepers by:
          </p>
          
          <ul className="md:text-[32px] text-[8px] list-disc pl-5 space-y-2">
            <li>Offering access to a network of certified vets via the app</li>
            <li>Sending vaccination and deworming reminders</li>
            <li>Providing step-by-step guides on animal nutrition and care</li>
            <li>Linking farmers to trusted sellers of livestock supplies</li>
            <li>Helping track and analyze animal health data</li>
          </ul>
          
          <div>
            <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
              🐖 Why It Matters
            </h2>
            
            <p className="md:text-[32px] text-[8px]">
              By using digital veterinary services, smallholder farmers can:
            </p>
            
            <ul className="md:text-[32px] text-[8px] list-disc font-bold pl-10 space-y-2">
              <li>Reduce livestock mortality</li>
              <li>Improve milk, meat, and egg production</li>
              <li>Prevent zoonotic disease spread</li>
              <li>Lower costs through preventive care</li>
              <li>Increase income and food security</li>
            </ul>
          </div>
        </div>
        
        <div className='flex justify-end items-center'>
          <div>
            <h1 className="text-[18px] md:text-[32px] font-bold mt-2 mb-4 text-black">
              Download App
            </h1>
            <div className="md:w-[450px]">
              <div className="flex flex-row justify-between space-x-2 items-center sm:space-y-0">
                <a href="https://play.google.com/store/apps/details?id=com.farmnet.africa&pcampaignid=web_share" alt="none">   
             <img src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751889690/google_PLAY-LOGO.WINE_emyxpu.png" className="h-[25px] md:w-[280px] md:h-[180px]" alt="App Store" />    
             <p className="md:text-[14px] text-[6px] md:-mt-12 md:pl-5 font-normal md:px-1">
             Click to download App
  </p>
     </a>
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744414874/image_scynki.png"
                  alt="Google Play"
                  className="h-[30px] md:h-[75px] object-contain"
                />
              </div>
            </div>
          </div>
        </div>
        <div className='md:h-10 h-5'></div>
      </div>
    </div>
  );
};

export default Solutions;